/**
 * AppVersionManager - Robuster Update-Mechanismus für Mobile & Desktop
 * 
 * Funktionsweise:
 * 1. Beim App-Start wird version.json geprüft
 * 2. Alle 60 Sekunden wird erneut geprüft
 * 3. Bei Version-Unterschied: Gezielter Reset von Auth/Cache-Daten
 * 
 * Löst folgende Probleme:
 * - Alte JWT Tokens in localStorage/sessionStorage
 * - Service Worker Cache / PWA Cache
 * - Cached Frontend Bundle führt zu API-Mismatch
 * - Alte redirect-States vom QR-Flow
 * 
 * WICHTIG: Nach einem Deploy müssen Nutzer NICHT manuell Cookies löschen!
 */
import { useEffect, useRef, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';

// Check-Intervall in Millisekunden (60 Sekunden)
const VERSION_CHECK_INTERVAL = 60 * 1000;

// Debounce um mehrfache Reloads zu verhindern
let isReloading = false;

// Keys die bei Version-Update entfernt werden sollen
const AUTH_STORAGE_KEYS = [
  'token',
  'auth_token',
  'user',
  'refresh_token',
  'returnAfterLogin',
  'qr_object_id',
  'qr_context',
  'app_cached_filters',
  'lastActivity'
];

/**
 * Zeigt eine Update-Benachrichtigung an
 */
const UpdateNotification = ({ visible }) => {
  if (!visible) return null;
  
  return createPortal(
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 99999
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '32px',
        textAlign: 'center',
        maxWidth: '90%',
        width: '320px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          margin: '0 auto 16px',
          borderRadius: '50%',
          backgroundColor: '#0E4C92',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
            <path d="M21 12a9 9 0 0 1-9 9m9-9a9 9 0 0 0-9-9m9 9H3m9 9a9 9 0 0 1-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9"/>
          </svg>
        </div>
        <h3 style={{ margin: '0 0 8px', fontSize: '18px', color: '#1e293b' }}>
          Update erkannt
        </h3>
        <p style={{ margin: 0, fontSize: '14px', color: '#64748b' }}>
          Wir laden die App neu...
        </p>
        <div style={{
          marginTop: '16px',
          width: '32px',
          height: '32px',
          margin: '16px auto 0',
          border: '3px solid #e5e7eb',
          borderTopColor: '#0E4C92',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>,
    document.body
  );
};

/**
 * Führt einen gezielten Reset der App-Daten durch
 * Entfernt nur App-bezogene Daten, nicht alle Cookies
 */
const performClientReset = async (newVersion) => {
  if (isReloading) return;
  isReloading = true;
  
  console.log('[VersionManager] Neue Version erkannt - starte Update...');
  
  try {
    // 1. Auth-bezogene localStorage Keys entfernen
    AUTH_STORAGE_KEYS.forEach(key => {
      try {
        localStorage.removeItem(key);
      } catch (e) {
        // Ignore
      }
    });
    console.log('[VersionManager] Auth-Daten entfernt');
    
    // 2. SessionStorage komplett leeren (enthält temp QR-Daten etc.)
    try {
      sessionStorage.clear();
    } catch (e) {
      // Ignore
    }
    console.log('[VersionManager] SessionStorage geleert');
    
    // 3. Service Worker Caches löschen
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => caches.delete(name)));
      console.log('[VersionManager] Caches gelöscht');
    }
    
    // 4. Service Worker deregistrieren
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const reg of registrations) {
        if (reg.waiting) {
          reg.waiting.postMessage('SKIP_WAITING');
        }
        await reg.unregister();
      }
      console.log('[VersionManager] Service Worker deregistriert');
    }
    
    // 5. Neue Version speichern
    localStorage.setItem('app_version', newVersion);
    
    // 6. Kurze Verzögerung für UX
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // 7. Redirect zu Login mit Grund
    window.location.href = '/login?reason=updated';
    
  } catch (error) {
    console.error('[VersionManager] Update-Fehler:', error);
    // Fallback: Einfacher Reload
    window.location.reload();
  }
};

/**
 * Prüft ob eine neue Version verfügbar ist
 */
const checkForUpdate = async () => {
  try {
    const response = await fetch(`/version.json?_=${Date.now()}`, {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache'
      }
    });
    
    if (!response.ok) {
      console.warn('[VersionManager] version.json nicht erreichbar');
      return { needsUpdate: false };
    }
    
    const data = await response.json();
    const serverVersion = data.version || data.buildTime;
    
    // Ignoriere Placeholder-Werte
    if (!serverVersion || serverVersion === '__BUILD_TIME__') {
      return { needsUpdate: false };
    }
    
    // Gespeicherte Version laden
    const storedVersion = localStorage.getItem('app_version');
    
    // Beim ersten Aufruf: Version speichern, kein Update
    if (!storedVersion) {
      localStorage.setItem('app_version', serverVersion);
      console.log(`[VersionManager] Initial version set: ${serverVersion}`);
      return { needsUpdate: false };
    }
    
    // Vergleiche Versionen
    if (serverVersion !== storedVersion) {
      console.log(`[VersionManager] Version changed: ${storedVersion} -> ${serverVersion}`);
      return { needsUpdate: true, newVersion: serverVersion };
    }
    
    return { needsUpdate: false };
  } catch (error) {
    console.warn('[VersionManager] Version check failed:', error.message);
    return { needsUpdate: false };
  }
};

/**
 * Auth Guard - Löscht Token bei 401/403 Fehlern
 * Wird global über axios Interceptor aufgerufen
 */
export const handleAuthError = (status) => {
  if (status === 401 || status === 403) {
    console.log('[VersionManager] Auth-Fehler erkannt, lösche Token...');
    
    // Token entfernen
    AUTH_STORAGE_KEYS.forEach(key => {
      try {
        localStorage.removeItem(key);
      } catch (e) {
        // Ignore
      }
    });
    
    try {
      sessionStorage.clear();
    } catch (e) {
      // Ignore
    }
    
    // Nur redirecten wenn nicht bereits auf Login-Seite
    if (!window.location.pathname.includes('/login')) {
      window.location.href = '/login?reason=session_expired';
    }
  }
};

/**
 * React Hook für den Version-Check
 */
export const useVersionCheck = () => {
  const [showNotification, setShowNotification] = useState(false);
  const intervalRef = useRef(null);
  const isCheckingRef = useRef(false);
  
  const runCheck = useCallback(async () => {
    if (isCheckingRef.current || isReloading) return;
    isCheckingRef.current = true;
    
    try {
      const { needsUpdate, newVersion } = await checkForUpdate();
      if (needsUpdate && newVersion) {
        setShowNotification(true);
        await performClientReset(newVersion);
      }
    } finally {
      isCheckingRef.current = false;
    }
  }, []);
  
  useEffect(() => {
    // Initial Check nach 5 Sekunden
    const initialTimeout = setTimeout(runCheck, 5000);
    
    // Regelmäßiger Check alle 60 Sekunden
    intervalRef.current = setInterval(runCheck, VERSION_CHECK_INTERVAL);
    
    // Cleanup
    return () => {
      clearTimeout(initialTimeout);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [runCheck]);
  
  return { showNotification };
};

/**
 * Komponente die den Version-Check aktiviert und UI anzeigt
 */
const AppVersionManager = () => {
  const { showNotification } = useVersionCheck();
  return <UpdateNotification visible={showNotification} />;
};

export default AppVersionManager;
